import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Loader2, Pencil, Trash2, Eye, EyeOff, Search, FileText, BarChart3, Share2, Radio } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * /admin/content — list all Insights articles (drafts + published).
 * Row-level: edit, toggle publish, delete.
 */
export default function AdminContent() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/content/articles");
      setRows(res.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const togglePublish = async (id) => {
    try {
      const res = await api.post(`/admin/content/articles/${id}/publish`);
      setRows((rs) => rs.map((r) => (r.id === id ? { ...r, status: res.data.status } : r)));
      toast.success(res.data.status === "published" ? "Published" : "Moved to draft");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const doDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/admin/content/articles/${toDelete.id}`);
      setRows((rs) => rs.filter((r) => r.id !== toDelete.id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setToDelete(null);
    }
  };

  const filtered = q.trim()
    ? rows.filter((r) => (r.title + " " + r.slug + " " + (r.sector_tags || []).join(" ")).toLowerCase().includes(q.toLowerCase()))
    : rows;

  // Aggregate analytics across all published articles
  const analytics = React.useMemo(() => {
    const published = rows.filter((r) => r.status === "published");
    const totalViews  = published.reduce((s, r) => s + (r.views  || 0), 0);
    const totalShares = published.reduce((s, r) => s + (r.shares || 0), 0);
    const top = [...published].sort((a, b) => (b.views || 0) - (a.views || 0))[0];

    // Aggregate views_by_source across ALL published articles
    const sourceTotals = {};
    for (const r of published) {
      const vbs = r.views_by_source || {};
      for (const [k, v] of Object.entries(vbs)) {
        sourceTotals[k] = (sourceTotals[k] || 0) + v;
      }
    }
    const sources = Object.entries(sourceTotals)
      .sort((a, b) => b[1] - a[1])
      .map(([source, count]) => ({
        source,
        count,
        pct: totalViews > 0 ? Math.round((count / totalViews) * 1000) / 10 : 0,
      }));

    return {
      count: published.length,
      totalViews,
      totalShares,
      shareRate: totalViews > 0 ? Math.round((totalShares / totalViews) * 1000) / 10 : 0,
      top: top?.views ? top : null,
      sources,
    };
  }, [rows]);

  return (
    <div className="space-y-6" data-testid="admin-content-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10.5px] font-semibold tracking-[0.22em] uppercase text-[#0055FF]">Insights engine</div>
          <h1 className="mt-1 text-[26px] tracking-[-0.02em] font-semibold text-slate-900">Articles</h1>
          <p className="mt-1 text-[13.5px] text-slate-500">
            Publish long-form pieces that surface on <Link to="/insights" className="underline text-slate-700">/insights</Link>. Written in markdown.
          </p>
        </div>
        <Button
          onClick={() => nav("/admin/content/new")}
          className="rounded-full bg-[#0055FF] hover:bg-[#0044CC] shadow-[0_8px_24px_rgba(0,85,255,0.35)]"
          data-testid="admin-content-new"
        >
          <Plus className="size-3.5 mr-1" /> New article
        </Button>
      </div>

      {/* Analytics strip */}
      {!loading && analytics.count > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="admin-content-analytics">
          <AnalyticsCard label="Published" value={analytics.count} Icon={FileText} tone="slate" />
          <AnalyticsCard label="Total views" value={analytics.totalViews.toLocaleString()} Icon={Eye} tone="brand" />
          <AnalyticsCard label="Total shares" value={analytics.totalShares.toLocaleString()} Icon={Share2} tone="green" sub={`${analytics.shareRate}% share rate`} />
          <AnalyticsCard
            label="Top article"
            value={analytics.top ? analytics.top.title : "—"}
            Icon={BarChart3}
            tone="muted"
            sub={analytics.top ? `${analytics.top.views.toLocaleString()} views` : "no views yet"}
            small
          />
        </div>
      )}

      {/* Traffic sources */}
      {!loading && analytics.totalViews > 0 && (
        <TrafficSources sources={analytics.sources} total={analytics.totalViews} />
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-slate-400" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search title, slug or tag…"
          className="pl-9 h-10"
          data-testid="admin-content-search"
        />
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        {loading ? (
          <div className="grid place-items-center py-16 text-slate-400"><Loader2 className="size-5 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-slate-500">
            <FileText className="size-6 mx-auto text-slate-300" />
            <div className="mt-3 text-[13.5px]">
              {rows.length === 0 ? "No articles yet — create the first one." : "No article matches your search."}
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="admin-content-table">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <Th>Title</Th>
                  <Th>Slug</Th>
                  <Th>Tags</Th>
                  <Th>Status</Th>
                  <Th className="text-right">Views</Th>
                  <Th className="text-right">Shares</Th>
                  <Th>Updated</Th>
                  <Th className="text-right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/40" data-testid={`admin-content-row-${r.slug}`}>
                    <Td>
                      <div className="font-semibold text-slate-900">{r.title}</div>
                      <div className="text-[11.5px] text-slate-500 mt-0.5 line-clamp-1">{r.excerpt}</div>
                    </Td>
                    <Td className="font-mono text-[12px] text-slate-500">/{r.slug}</Td>
                    <Td>
                      <div className="flex items-center gap-1 flex-wrap max-w-[220px]">
                        {(r.sector_tags || []).slice(0, 3).map((t) => (
                          <span key={t} className="inline-flex items-center rounded bg-slate-100 border border-slate-200 px-1.5 py-0.5 text-[9.5px] font-medium uppercase tracking-wide text-slate-600">
                            {t}
                          </span>
                        ))}
                        {(r.sector_tags || []).length > 3 && (
                          <span className="text-[10.5px] text-slate-400 font-mono">+{r.sector_tags.length - 3}</span>
                        )}
                      </div>
                    </Td>
                    <Td>
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10.5px] font-medium ${r.status === "published" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-50 text-slate-500 border border-slate-200"}`}>
                        {r.status}
                      </span>
                    </Td>
                    <Td className="text-right font-mono tabular-nums text-slate-700" data-testid={`admin-content-views-${r.slug}`}>
                      {(r.views || 0).toLocaleString()}
                    </Td>
                    <Td className="text-right font-mono tabular-nums text-slate-700" data-testid={`admin-content-shares-${r.slug}`}>
                      {(r.shares || 0).toLocaleString()}
                    </Td>
                    <Td className="text-slate-500">{fmt(r.updated_at)}</Td>
                    <Td className="text-right">
                      <div className="inline-flex gap-1">
                        <button
                          onClick={() => togglePublish(r.id)}
                          className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
                          title={r.status === "published" ? "Unpublish" : "Publish"}
                          data-testid={`admin-content-publish-${r.slug}`}
                        >
                          {r.status === "published" ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                        </button>
                        <Link
                          to={`/admin/content/${r.id}`}
                          className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
                          title="Edit"
                          data-testid={`admin-content-edit-${r.slug}`}
                        >
                          <Pencil className="size-4" />
                        </Link>
                        <button
                          onClick={() => setToDelete(r)}
                          className="p-1.5 rounded hover:bg-red-50 text-red-500"
                          title="Delete"
                          data-testid={`admin-content-delete-${r.slug}`}
                        >
                          <Trash2 className="size-4" />
                        </button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this article?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-semibold text-slate-900">{toDelete?.title}</span> will be permanently removed. This can&apos;t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-500 hover:bg-red-600">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
function Th({ children, className = "" }) { return <th className={`text-left px-4 py-2.5 text-[10.5px] font-semibold uppercase tracking-[0.14em] ${className}`}>{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-4 py-3 align-top ${className}`}>{children}</td>; }
function fmt(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
  } catch { return iso; }
}

function AnalyticsCard({ label, value, Icon, tone, sub, small }) {
  const toneCls = {
    brand:  "bg-[#0055FF]/[0.06] border-[#0055FF]/25 text-[#0055FF]",
    slate:  "bg-slate-50 border-slate-200 text-slate-700",
    green:  "bg-emerald-50 border-emerald-200 text-emerald-700",
    muted:  "bg-white border-slate-200 text-slate-700",
  }[tone] || "bg-white border-slate-200 text-slate-700";
  return (
    <div className={`rounded-xl border p-4 ${toneCls}`}>
      <div className="flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.18em] opacity-80">
        <Icon className="size-3" />
        {label}
      </div>
      <div className={`mt-1.5 font-semibold tabular-nums truncate ${small ? "text-[15px] leading-snug" : "text-[24px]"}`} title={typeof value === "string" ? value : undefined}>
        {value}
      </div>
      {sub && <div className="mt-1 text-[11px] opacity-70 truncate">{sub}</div>}
    </div>
  );
}

// Pretty labels + accent colours for each canonical traffic source.
const SOURCE_LABELS = {
  direct:      "Direct",
  google:      "Google",
  bing:        "Bing",
  duckduckgo:  "DuckDuckGo",
  yahoo:       "Yahoo",
  twitter:     "Twitter · X",
  linkedin:    "LinkedIn",
  facebook:    "Facebook",
  instagram:   "Instagram",
  reddit:      "Reddit",
  youtube:     "YouTube",
  producthunt: "Product Hunt",
  hackernews:  "Hacker News",
  github:      "GitHub",
  medium:      "Medium",
  substack:    "Substack",
};

function labelForSource(key) {
  return SOURCE_LABELS[key] || key.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function TrafficSources({ sources, total }) {
  // Palette of accent colours cycled through the top sources
  const PALETTE = ["#0055FF", "#7da9ff", "#34d399", "#f59e0b", "#a855f7", "#ec4899", "#14b8a6", "#94a3b8"];
  const top = sources.slice(0, 8);
  const other = sources.slice(8).reduce((s, x) => s + x.count, 0);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="admin-content-traffic-sources">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div className="inline-flex items-center gap-2 text-[10.5px] font-semibold tracking-[0.22em] uppercase text-slate-600">
          <Radio className="size-3 text-[#0055FF]" />
          Traffic sources
        </div>
        <div className="text-[11px] text-slate-400 font-mono">
          {sources.length} channel{sources.length === 1 ? "" : "s"} · {total.toLocaleString()} views total
        </div>
      </div>

      <div className="space-y-2">
        {top.map((s, i) => {
          const color = PALETTE[i % PALETTE.length];
          return (
            <div key={s.source} className="flex items-center gap-3" data-testid={`admin-content-source-${s.source}`}>
              <span className="w-32 shrink-0 text-[13px] font-medium text-slate-800 truncate" title={s.source}>
                <span className="inline-block size-1.5 rounded-full mr-2" style={{ background: color }} />
                {labelForSource(s.source)}
              </span>
              <div className="flex-1 relative h-2 rounded-full bg-slate-100 overflow-hidden">
                <span
                  className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-500 ease-out"
                  style={{ width: `${Math.max(s.pct, 1)}%`, background: `linear-gradient(to right, ${color}, ${color}CC)` }}
                />
              </div>
              <span className="w-24 shrink-0 text-right text-[12px] font-mono tabular-nums text-slate-600">
                {s.count.toLocaleString()}
                <span className="ml-1.5 text-slate-400">{s.pct}%</span>
              </span>
            </div>
          );
        })}
        {other > 0 && (
          <div className="flex items-center gap-3 text-slate-500">
            <span className="w-32 shrink-0 text-[13px] italic">
              <span className="inline-block size-1.5 rounded-full mr-2 bg-slate-300" />
              Other channels
            </span>
            <div className="flex-1" />
            <span className="w-24 shrink-0 text-right text-[12px] font-mono tabular-nums">
              {other.toLocaleString()}
              <span className="ml-1.5 text-slate-400">{Math.round((other / total) * 1000) / 10}%</span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
